import { GameState } from "../plongeon";

interface Animation {
    frameCount: number;
    frameDelay: number;
    sourceX: number;
}

enum PlongeurStateEnum {
    preparing = "preparing",
    idle = "idle",
    jumping = "jumping",
    diveIn = "dive in",
    swimming = "swimming",
}

export interface ScoreDetail {
    saltos: number,
    angleDiff: number,
    indication: string;
    precision?: number,
    position: number,
    precisionMultiplier?: number,
    positionMultiplier?: number,
}

export default class Plongeur {
    readonly gameState: GameState;

    readonly spriteWidth = 20;
    readonly spriteHeight = 35;
    readonly spriteImg = new Image();
    readonly gravity = 1;
    readonly basePosX = 160;
    readonly basePosY = 182;

    readonly animations: Record<PlongeurStateEnum.preparing | PlongeurStateEnum.diveIn, Animation> = {
        [PlongeurStateEnum.preparing]: {
            frameCount: 4,
            frameDelay: 200,
            sourceX: 0
        },
        [PlongeurStateEnum.diveIn]: {
            frameCount: 3,
            frameDelay: 200,
            sourceX: this.spriteWidth
        }
    }

    currentFrame = 0;
    frameTimer = 0;
    playerPos = {x: 0, y: 0, r: 0};
    playerSpeed = {x: 0, y: 0, r: 0};
    onJumpFinished = () => {};

    score: ScoreDetail | undefined;

    state: PlongeurStateEnum = PlongeurStateEnum.preparing;


    constructor(gameState: GameState) {
        this.spriteImg.src = '/plongeur.png';
        this.gameState = gameState;
    }

    draw(deltaTime: number, ctx: CanvasRenderingContext2D, zoom: number) {
        if (!this.spriteImg.complete) return;

        switch (this.state) {
            case "preparing":
                this.drawPreparationAnimation(deltaTime, ctx, zoom);
                break;
            case "idle":
                this.drawStanding(ctx, zoom);
                break;
            case "jumping":
            case "dive in":
                this.drawJumpAnimation(deltaTime, ctx, zoom);
                break;
            case "swimming":

        }
        
    }

    goIdle(heightForce: number) {
        this.state = PlongeurStateEnum.idle;
        this.currentFrame = 0;
        if (heightForce > 50) this.currentFrame = 1;
        if (heightForce > 80) this.currentFrame = 2;
    }

    jump(onJumpFinished: () => void) {
        if (this.state === PlongeurStateEnum.jumping) {
            this.diveIn();
            return;
        }
        if (this.state !== PlongeurStateEnum.idle || this.gameState.rotationSpeed === null || this.gameState.heightForce === null) return;

        this.onJumpFinished = onJumpFinished;
        this.frameTimer = 0;
        this.currentFrame = 0;
        this.state = PlongeurStateEnum.jumping;
        this.playerSpeed = {
            x: 300,
            y: this.gameState.heightForce * 10,
            r: this.gameState.rotationSpeed
        };
    }

    getScore() {
        if (!this.score) return 0;

        this.score.precision = Math.round(Math.abs(90 - this.score.angleDiff)*10/9);
        const precisionMultiplier = Math.pow((160-this.score.angleDiff)/35, 4) + this.score.angleDiff - 100;

        this.score.precisionMultiplier = Math.round(precisionMultiplier)/100;
        let baseScore = 1000 * (this.score.saltos + 1) * precisionMultiplier / 100;

        if (this.score.position === 0) this.score.positionMultiplier = 1;
        if (this.score.position === 1) this.score.positionMultiplier = 0.5;
        if (this.score.position === 2) this.score.positionMultiplier = 1.5;

        return Math.round(baseScore * (this.score.positionMultiplier as number));
    }

    private diveIn() {
        this.state = PlongeurStateEnum.diveIn;
    }

    private drawPreparationAnimation(deltaTime: number, ctx: CanvasRenderingContext2D, zoom: number) {
        this.frameTimer += deltaTime;

        const {frameDelay, frameCount} = this.animations[PlongeurStateEnum.preparing];

        if (this.frameTimer >= frameDelay) {
            this.frameTimer = 0;
            this.currentFrame = (this.currentFrame + 1) % frameCount;
        }
        
        this.drawStanding(ctx, zoom);
    }

    private drawStanding(ctx: CanvasRenderingContext2D, zoom: number) {
        ctx.drawImage(
            this.spriteImg,
            0, this.currentFrame * this.spriteHeight, // source x, y
            this.spriteWidth, this.spriteHeight, // source width, height
            zoom * this.basePosX, zoom * this.basePosY, // destination x, y (on the pillar)
            zoom * this.spriteWidth, zoom * this.spriteHeight // destination width, height
        );
    }

    private drawJumpAnimation(deltaTime: number, ctx: CanvasRenderingContext2D, zoom: number) {
        this.playerPos.x += this.playerSpeed.x * deltaTime / 10000;
        this.playerPos.y += this.playerSpeed.y * deltaTime / 10000;
        this.playerPos.r += this.playerSpeed.r * deltaTime / 10000;
        this.playerSpeed.y -= this.gravity * deltaTime;

        const {frameDelay, frameCount, sourceX} = this.animations[PlongeurStateEnum.diveIn];

        if (this.state === PlongeurStateEnum.diveIn) {
            this.frameTimer += deltaTime;

            if (this.frameTimer >= frameDelay) {
                this.playerSpeed.r = this.playerSpeed.r * Math.pow(0.8, deltaTime/10);
                this.frameTimer = 0;
                this.currentFrame = Math.min(this.currentFrame + 1, frameCount - 1);
            }
        }

        if (this.playerPos.y <= -75 && !this.score) {
            const rDeg = this.playerPos.r / (2*Math.PI) * 360;
            const angleDiff = Math.min(
                Math.abs(170 - (rDeg % 360)),
                Math.abs((rDeg % 360) - 170)
            );

            let indication: string;

            const angleDiffIfMoreRotation = Math.min(
                Math.abs(170 - ((rDeg + 1) % 360)),
                Math.abs(((rDeg + 1) % 360) - 170)
            );

            if (angleDiff < 90) {
                // plus de rotation => moins de diff => trop en arrière
                indication = angleDiff - angleDiffIfMoreRotation > 0 ? "Un peu trop en arrière" : "Un peu trop en avant";
            } else {
                const angleDiffToInverted = 180 - angleDiff;
                const angleDiffToInvertedIfMoreRotation = 180 - angleDiffIfMoreRotation;
                indication = angleDiffToInverted - angleDiffToInvertedIfMoreRotation > 0 ? "Un peu trop en arrière pour faire la frite" : "Un peu trop en avant pour faire la frite";
            }
            if (angleDiff < 15) indication = "Joli plongeon !";
            if (angleDiff < 5) indication = "Plongeon parfait !";
            if (angleDiff > 165) indication = "Joli frite !";
            if (angleDiff > 175) indication = "Frite parfaite !";


            if (angleDiff > 60 && angleDiff < 120) {
                indication = angleDiff - angleDiffIfMoreRotation > 0 ? "Joli plat ! pas trop mal au ventre ?" : "Joli plat ! pas trop mal au dos ?";
            }

            this.score = {
                saltos: Math.floor(rDeg / 360),
                angleDiff,
                indication,
                position: this.currentFrame
            };
            this.onJumpFinished();
        }

        if (this.playerPos.y <= -100) {
            this.state = PlongeurStateEnum.swimming;
        }

        const posX = zoom * (this.basePosX + this.playerPos.x);
        const posY = zoom * (this.basePosY - this.playerPos.y);
        const rot = this.playerPos.r;
        
        const centerX = posX + (zoom * 12);
        const centerY = posY + (zoom * 25);

        ctx.translate(centerX, centerY);
        ctx.rotate(rot);
        ctx.translate(-centerX, -centerY);
        ctx.drawImage(
            this.spriteImg,
            sourceX, this.currentFrame * this.spriteHeight, // source x, y
            this.spriteWidth, this.spriteHeight, // source width, height
            posX, posY, // destination x, y
            zoom * this.spriteWidth, zoom * this.spriteHeight // destination width, height
        );
        ctx.setTransform(1, 0, 0, 1, 0, 0);
    }
}